import React, { useState, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';
import './index.scss';

/**
 * AntAIUpload — Upload file → AI trích xuất → Review popup 2 panel → Tạo phiếu
 *
 * Usage trong AntVoucher page:
 *   <AntAIUpload ref={(ref) => (this.aiUpload = ref)} targetVoucher="APOrder" session={session} />
 *   // Trong onAction: this.aiUpload.show({ voucherType: 'APOrder' })
 *
 * Hoặc trigger từ toolbar:
 *   this.voucher.showPopup(this.aiUpload)
 */
var aiAPI = null;
function getAiAPI() {
    if (!aiAPI) aiAPI = new AntAPI('ai', 'AIJobQueue');
    return aiAPI;
}

var voucherAPI = null;
function getVoucherAPI() {
    if (!voucherAPI) voucherAPI = new AntAPI('ai', 'AIJobQueue');
    return voucherAPI;
}

var AntAIUpload = forwardRef(function (props, ref) {
    var [visible, setVisible] = useState(false);
    var [step, setStep] = useState('upload'); // upload | processing | review
    var [file, setFile] = useState(null);
    var [filePreviewUrl, setFilePreviewUrl] = useState(null);
    var [extractedData, setExtractedData] = useState(null);
    var [preparedData, setPreparedData] = useState(null);
    var [error, setError] = useState(null);
    var [creating, setCreating] = useState(false);
    var fileInputRef = useRef(null);
    var contextRef = useRef({});

    useImperativeHandle(ref, function () {
        return {
            show: function (data) {
                contextRef.current = data || {};
                setVisible(true);
                setStep('upload');
                setFile(null);
                setFilePreviewUrl(null);
                setExtractedData(null);
                setPreparedData(null);
                setError(null);
                setCreating(false);
            },
            close: function () { setVisible(false); }
        };
    });

    // File chọn
    var handleFileSelect = useCallback(function (e) {
        var files = e.target.files;
        if (!files || files.length === 0) return;
        var f = files[0];
        setFile(f);
        setError(null);

        // Preview URL
        if (f.type.startsWith('image/')) {
            setFilePreviewUrl(URL.createObjectURL(f));
        } else {
            setFilePreviewUrl(null);
        }

        // Step 1: Upload file lên server → lấy URL
        // Step 2: Gọi AI inlineProcess với URL
        setStep('processing');

        var mediaAPI = new AntAPI('media', 'MEDDocument');
        var formData = new FormData();
        formData.append('files', f);
        formData.append('code', 'AI-UPLOAD');

        mediaAPI.upload('/' + mediaAPI.routeURL + '/upload', formData, function (uploadResult) {
            var uploadData = (uploadResult && uploadResult.data) || uploadResult || {};

            // Lấy file URL từ upload response
            var fileUrl = null;
            if (uploadData.rows && uploadData.rows.length > 0) {
                fileUrl = uploadData.rows[0].full_path || uploadData.rows[0].file_url;
            } else if (uploadData.full_path) {
                fileUrl = uploadData.full_path;
            } else if (uploadData.url) {
                fileUrl = uploadData.url;
            }

            if (!fileUrl) {
                setError('Không thể upload file');
                setStep('upload');
                return;
            }

            setFilePreviewUrl(fileUrl);

            // Step 2: Gọi AI extract
            var api = getAiAPI();
            api.post('/' + api.routeURL + '/inlineProcess', {
                file_url: fileUrl,
                file_name: f.name,
                file_mime: f.type,
                document_type: contextRef.current.documentType || props.documentType || 'invoice',
                target_voucher: contextRef.current.targetVoucher || props.targetVoucher
            }, function (result) {
                var data = (result && result.data) || result || {};
                if (data.error) {
                    setError(data.message || 'Lỗi trích xuất');
                    setStep('upload');
                    return;
                }

                setExtractedData(data.extracted || data);
                setStep('review');

                // Prepare voucher data
                var targetVoucher = contextRef.current.targetVoucher || props.targetVoucher;
                if (targetVoucher && data.extracted) {
                    var vApi = getVoucherAPI();
                    vApi.post('/' + vApi.routeURL + '/prepareVoucherData', {
                        extractedData: data.extracted,
                        targetVoucher: targetVoucher
                    }, function (prepResult) {
                        var prepData = (prepResult && prepResult.data) || prepResult || {};
                        if (!prepData.error) setPreparedData(prepData);
                    });
                }
            });
        });

        e.target.value = '';
    }, [props.targetVoucher]);

    // Tạo phiếu từ extracted data
    var handleCreate = useCallback(function () {
        if (!extractedData || creating) return;
        var targetVoucher = contextRef.current.targetVoucher || props.targetVoucher;
        if (!targetVoucher) { setError('Chưa chọn loại phiếu đích'); return; }

        setCreating(true);
        var vApi = getVoucherAPI();
        vApi.post('/' + vApi.routeURL + '/createFromExtractedData', {
            extractedData: extractedData,
            targetVoucher: targetVoucher
        }, function (result) {
            setCreating(false);
            var data = (result && result.data) || result || {};
            if (data.error) {
                setError(data.message || 'Lỗi tạo phiếu');
                return;
            }
            // Thành công → đóng popup, callback
            if (props.onCreated) props.onCreated(data);
            setVisible(false);
        });
    }, [extractedData, creating, props.targetVoucher, props.onCreated]);

    var handleClose = useCallback(function () { setVisible(false); }, []);

    if (!visible) return null;

    return (
        <div className="ant-ai-upload-overlay" onClick={handleClose}>
            <div className="ant-ai-upload-popup" onClick={function (e) { e.stopPropagation(); }}>
                {/* Header */}
                <div className="ant-ai-upload-header">
                    <span className="ant-ai-upload-title">
                        {AntLocalization.translateText({ vi: 'Trích xuất tài liệu bằng AI', en: 'AI Document Extraction', fr: 'Extraction de documents par IA', es: 'Extracción de documentos con IA', id: 'Ekstraksi dokumen dengan AI', ja: 'AIによる書類抽出', zh: 'AI 文档提取', ko: 'AI 문서 추출', hi: 'AI से दस्तावेज़ निष्कर्षण', ar: 'استخراج المستندات عبر AI' })}
                    </span>
                    <button className="ant-ai-upload-close" onClick={handleClose}>&times;</button>
                </div>

                {/* Content: 2 panels */}
                <div className="ant-ai-upload-body">
                    {/* Panel trái: File */}
                    <div className="ant-ai-upload-left">
                        {step === 'upload' && (
                            <div className="ant-ai-upload-dropzone" onClick={function () { fileInputRef.current && fileInputRef.current.click(); }}>
                                <input ref={fileInputRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleFileSelect} />
                                <div className="ant-ai-upload-icon">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                </div>
                                <div className="ant-ai-upload-text">
                                    {AntLocalization.translateText({ vi: 'Chọn hoá đơn, phiếu thu, biên lai...', en: 'Select invoice, receipt, bill...', fr: 'Choisir une facture, un reçu, une note...', es: 'Seleccione factura, recibo, comprobante...', id: 'Pilih faktur, kuitansi, tagihan...', ja: '請求書・領収書・レシートを選択...', zh: '选择发票、收据、账单...', ko: '송장, 영수증, 청구서 선택...', hi: 'इनवॉइस, रसीद, बिल चुनें...', ar: 'اختر فاتورة أو إيصالًا أو سند قبض...' })}
                                </div>
                                <div className="ant-ai-upload-hint">PDF, JPG, PNG</div>
                            </div>
                        )}

                        {step === 'processing' && (
                            <div className="ant-ai-upload-processing">
                                <div className="ant-ai-upload-spinner" />
                                <div className="ant-ai-upload-text">
                                    {AntLocalization.translateText({ vi: 'AI đang trích xuất dữ liệu...', en: 'AI is extracting data...', fr: 'IA extrait les données...', es: 'IA está extrayendo los datos...', id: 'AI sedang mengekstrak data...', ja: 'AIがデータを抽出中...', zh: 'AI 正在提取数据...', ko: 'AI가 데이터를 추출하는 중...', hi: 'AI डेटा निकाल रहा है...', ar: 'AI يستخرج البيانات...' })}
                                </div>
                                {file && <div className="ant-ai-upload-hint">{file.name}</div>}
                            </div>
                        )}

                        {step === 'review' && (
                            <div className="ant-ai-upload-preview">
                                {filePreviewUrl && <img src={filePreviewUrl} alt="preview" className="ant-ai-upload-img" />}
                                {!filePreviewUrl && file && (
                                    <div className="ant-ai-upload-file-info">
                                        <div className="ant-ai-upload-file-name">{file.name}</div>
                                        <div className="ant-ai-upload-hint">{Math.round((file.size || 0) / 1024)} KB</div>
                                    </div>
                                )}
                                <button className="ant-ai-upload-retry" onClick={function () { setStep('upload'); setExtractedData(null); setPreparedData(null); }}>
                                    {AntLocalization.translateText({ vi: 'Chọn file khác', en: 'Choose another file', fr: 'Choisir autre fichier', es: 'Elegir otro archivo', id: 'Pilih file lain', ja: '別のファイルを選択', zh: '选择其他文件', ko: '다른 파일 선택', hi: 'दूसरी फ़ाइल चुनें', ar: 'اختر ملفًا آخر' })}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Panel phải: Extracted data */}
                    <div className="ant-ai-upload-right">
                        {error && <div className="ant-ai-upload-error">{error}</div>}

                        {step === 'review' && extractedData && (
                            <div className="ant-ai-upload-extracted">
                                <div className="ant-ai-upload-section-title">
                                    {AntLocalization.translateText({ vi: 'Dữ liệu trích xuất', en: 'Extracted Data', fr: 'Données extraites', es: 'Datos extraídos', id: 'Data hasil ekstraksi', ja: '抽出データ', zh: '提取的数据', ko: '추출 데이터', hi: 'निकाला गया डेटा', ar: 'البيانات المستخرجة' })}
                                </div>
                                <div className="ant-ai-upload-json">
                                    <pre>{JSON.stringify(extractedData, null, 2)}</pre>
                                </div>

                                {preparedData && preparedData.warnings && preparedData.warnings.length > 0 && (
                                    <div className="ant-ai-upload-warnings">
                                        {preparedData.warnings.map(function (w, i) {
                                            return <div key={i} className="ant-ai-upload-warning">{w.message || w.type}</div>;
                                        })}
                                    </div>
                                )}

                                {extractedData.confidence != null && (
                                    <div className="ant-ai-upload-confidence">
                                        {AntLocalization.translateText({ vi: 'Độ tin cậy', en: 'Confidence', fr: 'Confiance', es: 'Confianza', id: 'Keyakinan', ja: '信頼度', zh: '置信度', ko: '신뢰도', hi: 'विश्वसनीयता', ar: 'الثقة' })}: <strong>{Math.round((extractedData.confidence || 0) * 100)}%</strong>
                                    </div>
                                )}
                            </div>
                        )}

                        {step === 'upload' && !error && (
                            <div className="ant-ai-upload-empty-right">
                                {AntLocalization.translateText({ vi: 'Chọn file để bắt đầu trích xuất', en: 'Select a file to start extraction', fr: 'Choisissez un fichier pour l\'extraction', es: 'Seleccione un archivo para extraer', id: 'Pilih file untuk memulai ekstraksi', ja: 'ファイルを選択して抽出を開始', zh: '选择文件以开始提取', ko: '파일을 선택해 추출을 시작하세요', hi: 'निष्कर्षण शुरू करने हेतु फ़ाइल चुनें', ar: 'اختر ملفًا لبدء الاستخراج' })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                {step === 'review' && (
                    <div className="ant-ai-upload-footer">
                        <button className="ant-ai-upload-btn ant-ai-upload-btn-cancel" onClick={handleClose}>
                            {AntLocalization.translateText({ vi: 'Đóng', en: 'Close', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' })}
                        </button>
                        <button className="ant-ai-upload-btn ant-ai-upload-btn-create" onClick={handleCreate} disabled={creating}>
                            {creating
                                ? AntLocalization.translateText({ vi: 'Đang tạo...', en: 'Creating...', fr: 'Création...', es: 'Creando...', id: 'Membuat...', ja: '作成中...', zh: '创建中...', ko: '생성 중...', hi: 'बन रहा है...', ar: 'قيد الإنشاء...' })
                                : AntLocalization.translateText({ vi: 'Tạo phiếu', en: 'Create Voucher', fr: 'Créer pièce', es: 'Crear documento', id: 'Buat voucher', ja: '伝票作成', zh: '创建单据', ko: '전표 생성', hi: 'वाउचर बनाएँ', ar: 'إنشاء سند' })
                            }
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});

AntAIUpload.displayName = 'AntAIUpload';
export default AntAIUpload;
